import { merge} from 'lodash';
import Assessment from '../assessment';
import AssessmentResult from '../../../values/AssessmentResult';
import {META_DESCRIPTION_KEYWORD_ID, META_TAG_POINTS} from '@axyseo/const/analysis';

/**
 * Assessment for checking the keyword matches in the meta description.
 */
class MetaDescriptionKeywordAssessment extends Assessment {
  /**
   * Sets the identifier and the config.
   *
   * @param {Object} [config] The configuration to use.
   * @param {number} [config.parameters.recommendedMinimum] The recommended minimum of keyword occurrences in the meta description.
   * @param {number} [config.scores.good] The score to return if there are enough keyword occurrences in the meta description.
   * @param {number} [config.scores.bad] The score to return if there aren't enough keyword occurrences in the meta description.
   * @param {string} [config.url] The URL to the relevant article on Yoast.com.
   *
   * @returns {void}
   */
  constructor(config = {}) {
    super();

    const defaultConfig = {
      id: META_DESCRIPTION_KEYWORD_ID,
      priority: 'high',
      fixPosition: 'meta description',
      ctaType: 'fix',
      docUrl: 'https://help.seoon.io/seo/seo-checklist',
      title: 'Meta description keyword',
      content: {
        bad: 'No keywords found in meta description. Add at least one to improve keyword rank.',
        good: 'Keywords are included meta description.',
        improve: ''
      }
    };

    this.identifier = META_DESCRIPTION_KEYWORD_ID;
    this._config = merge(defaultConfig, config);
  }

  /**
   * Runs the metaDescriptionKeyword researcher and based on this, returns an assessment result with score.
   *
   * @param {Paper}      paper      The paper to use for the assessment.
   * @param {Researcher} researcher The researcher used for calling research.
   *
   * @returns {AssessmentResult} The assessment result.
   */
  getResult({paper, researcher}) {
    this._keyphraseCounts = researcher.getResearch('metaDescriptionKeyword');

    const assessmentResult = new AssessmentResult({config: this._config});
    const calculatedResult = this.calculateResult(paper);

    assessmentResult.setScore(calculatedResult.score);
    assessmentResult.setStatus(calculatedResult.status);

    return assessmentResult;
  }

  /**
   *
   * @returns {{score: number, status: string}}
   */
  calculateResult() {
    let status = 'bad';
    if (this._keyphraseCounts > 0) {
      status = 'good';
    }
    const score = this.getScore(META_TAG_POINTS, status);

    return {
      score,
      status
    };
  }

  /**
   * Checks whether the paper has a keyword and a meta description.
   *
   * @param {Paper} paper The paper to use for the assessment.
   *
   * @returns {boolean} True if the paper has a keyword and a meta description.
   */
  isApplicable(paper) {
    return true;
  }
}

export default MetaDescriptionKeywordAssessment;
